import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CheckSquare, ExternalLink, Folder, GitPullRequest, Tag } from 'lucide-react';
import {
  ASSOCIATION_KIND_AGENT_GROUP,
  ASSOCIATION_KIND_AGENT_LABEL,
  ASSOCIATION_KIND_PULL_REQUEST,
  associationDataString,
  associationUrl,
  agentGroupRefName,
  agentLabelRefName,
  isLivePrState,
  narrowPrAssociationRole,
  parsePrExternalId,
  prAssociationSnapshot,
  prAssociationState,
  type AgentAssociationRef,
} from '@reflex/shared';
import { Badge, badgeVariants } from '@reflex/ui/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import { ASSOCIATION_CHIP_BADGE_CLASSNAME } from '@reflex/ui/components/shared/AssociationChip';
import { cn } from '@reflex/ui/lib/utils';
import { usePlugins } from '@/hooks/config/usePlugins';
import { PULL_REQUEST_CHIP_BADGE_CLASSNAME } from '@reflex/ui/components/shared/PullRequestChip';
import { PR_STATE_META } from '@reflex/ui/components/shared/pr-state-presentation';
import { AgentPRChip } from '@/components/agent/AgentPRChip';
import { GenericAssociationChip } from '@/components/agent/GenericAssociationChip';
import { AgentGroupChip } from '@/components/agent/AgentGroupChip';
import { AgentLabelChip } from '@/components/agent/AgentLabelChip';
import {
  PullRequestBatchProvider,
  type PullRequestRef,
} from '@reflex/plugin-github/web/pull-request-batch';

export interface AgentAssociationChipsProps {
  /**
   * Associations in display order — pass the output of
   * `agentDisplayAssociations(agent)` so pull requests lead.
   */
  associations: AgentAssociationRef[];
  /** Hard cap on inline chips regardless of space. */
  max?: number;
  /**
   * The agent the strip belongs to. Only the group chip needs it (to take
   * this agent out of a group); every other kind links somewhere external and
   * is identity-free.
   */
  agentId?: string;
  /**
   * Whether `open` PR chips verify their live state against GitHub eagerly on
   * mount. Defaults to `true` (the focused stream header and detail identity
   * want a stale-open PR to reconcile without a hover). List rows pass `false`
   * so a page of N agents doesn't fire N background `pulls/:number` fetches on
   * load — the Sentry `AGENT-FLOW-WEB-4F` N+1. See
   * {@link AgentPRChip} for the lazy-vs-eager tradeoff.
   */
  eagerVerifyOpenPrs?: boolean;
  className?: string;
}

/** Gap between chips (`gap-1`). */
const CHIP_GAP = 4;

/**
 * Short label a chip renders: `#42` for PRs, the group's name for a group
 * (its external id is an opaque `agp_…`), the external id otherwise.
 */
function associationLabel(ref: AgentAssociationRef): string {
  if (ref.kind === ASSOCIATION_KIND_PULL_REQUEST) {
    const number = parsePrExternalId(ref.externalId)?.number;
    return number ? `#${number}` : 'PR';
  }
  if (ref.kind === ASSOCIATION_KIND_AGENT_GROUP) return agentGroupRefName(ref);
  if (ref.kind === ASSOCIATION_KIND_AGENT_LABEL) return agentLabelRefName(ref);
  return ref.externalId;
}

function associationKey(ref: AgentAssociationRef): string {
  return `${ref.kind}:${ref.role}:${ref.externalId}`;
}

/**
 * The still-live (`open` / `draft`) PR refs among the chips a strip actually
 * renders (the `visible` slice before the "+N" overflow), so the eager
 * surfaces can batch their
 * live-state fetch in one `pulls/batch` request instead of one
 * `pulls/:number` per chip (the Sentry `AGENT-FLOW-WEB-4F` N+1). Limited to
 * `visible` on purpose: the overflow rows render plain titles (no chip, no
 * fetch), so batching them would fetch live state nothing reads. Returns `[]`
 * for list rows (`eagerVerifyOpenPrs === false`) — they verify lazily on hover.
 */
function openPrRefsForChips(
  visible: AgentAssociationRef[],
  eagerVerifyOpenPrs: boolean | undefined,
): PullRequestRef[] {
  if (eagerVerifyOpenPrs === false) return [];
  const refs: PullRequestRef[] = [];
  for (const ref of visible) {
    if (ref.kind !== ASSOCIATION_KIND_PULL_REQUEST) continue;
    if (!isLivePrState(prAssociationState(ref))) continue;
    const parsed = parsePrExternalId(ref.externalId);
    if (!parsed) continue;
    const [owner, repo] = parsed.repoSlug.split('/');
    if (!owner || !repo) continue;
    refs.push({ owner, repo, number: parsed.number });
  }
  return refs;
}

interface FitMetrics {
  /** Width the strip is allowed to occupy (the clamped root width). */
  available: number;
  /** Rendered width of each candidate chip with its label, in display order. */
  chipWidths: number[];
  /**
   * Rendered width of each candidate chip collapsed to its icon (no label), in
   * display order. The fallback the strip drops to when even one labelled chip
   * can't fit the row.
   */
  chipIconWidths: number[];
  /** Rendered width of the "+N" overflow trigger. */
  moreWidth: number;
}

function sameMetrics(a: FitMetrics, b: FitMetrics): boolean {
  return (
    a.available === b.available &&
    a.moreWidth === b.moreWidth &&
    a.chipWidths.length === b.chipWidths.length &&
    a.chipWidths.every((w, i) => w === b.chipWidths[i]) &&
    a.chipIconWidths.length === b.chipIconWidths.length &&
    a.chipIconWidths.every((w, i) => w === b.chipIconWidths[i])
  );
}

/**
 * How many chips fit the strip. At least one chip always renders (the
 * highest-priority association is never hidden), at most `cap`; everything
 * else collapses behind the "+N" trigger. `widths` are the per-chip widths to
 * fit against — the labelled widths normally, the icon-only widths once the
 * strip has collapsed.
 */
function fitCount(
  total: number,
  cap: number,
  metrics: FitMetrics | null,
  widths: number[] | undefined,
): number {
  const capped = Math.min(total, cap);
  // No measurement yet (first paint, hidden ancestor) or stale measurement
  // after the association set changed: render the cap until a measure lands.
  if (!metrics || !widths || widths.length < capped || metrics.available <= 0) {
    return capped;
  }
  const { available, moreWidth } = metrics;
  const chipWidths = widths;
  const fullWidth =
    chipWidths.slice(0, capped).reduce((sum, w) => sum + w, 0) +
    CHIP_GAP * Math.max(0, capped - 1) +
    (total > capped ? moreWidth + CHIP_GAP : 0);
  // 1px slack absorbs fractional widths on zoomed/hidpi screens.
  if (fullWidth <= available + 1) return capped;

  const budget = available - moreWidth - CHIP_GAP;
  let used = 0;
  let visible = 0;
  for (let i = 0; i < capped; i++) {
    const next = used + (i > 0 ? CHIP_GAP : 0) + chipWidths[i];
    if (visible > 0 && next > budget) break;
    used = next;
    visible += 1;
  }
  return Math.max(1, visible);
}

/**
 * Width lookalikes of a chip for the hidden measurement row: one labelled, one
 * collapsed to its icon. The label renders via CSS `content: attr(data-label)`
 * so it occupies its exact width without duplicating the chip text in the DOM
 * (which would confuse find-in-page and text queries) — the
 * {@link SegmentedTabs} technique. The classes are the shared chip badge
 * classes (`AssociationChip` shell / the PR chip's exported set), so widths
 * can't drift for chips built on them.
 */
function MeasureChip({ refItem }: { refItem: AgentAssociationRef }) {
  const isPr = refItem.kind === ASSOCIATION_KIND_PULL_REQUEST;
  const Icon = isPr
    ? GitPullRequest
    : refItem.kind === ASSOCIATION_KIND_AGENT_GROUP
      ? Folder
      : refItem.kind === ASSOCIATION_KIND_AGENT_LABEL
        ? Tag
        : CheckSquare;
  const badgeClass = cn(
    badgeVariants({ variant: 'outline' }),
    isPr ? PULL_REQUEST_CHIP_BADGE_CLASSNAME : ASSOCIATION_CHIP_BADGE_CLASSNAME,
  );
  return (
    <>
      <span
        data-measure="chip"
        data-label={associationLabel(refItem)}
        className={cn(badgeClass, 'before:content-[attr(data-label)]')}
      >
        <Icon className="h-2.5 w-2.5" aria-hidden="true" />
      </span>
      {/* The icon-only lookalike is measured but taken out of flow: it must not
          add to the measure row's intrinsic width (which pins the root), only
          expose the width the chip collapses to. */}
      <span data-measure="chip-icon" className={cn(badgeClass, 'absolute')}>
        <Icon className="h-2.5 w-2.5" aria-hidden="true" />
      </span>
    </>
  );
}

/**
 * The agent's linked work items as a single adaptive chip strip, each kind
 * rendered by its own chip: the core PR chip for `pull_request` (always
 * first), the chip the owning plugin registered
 * (`PluginWebManifest.associationChips`) for plugin kinds, and a generic
 * fallback for kinds with no registered renderer. At most
 * {@link AgentAssociationChipsProps.max} (default 3) chips show inline; the
 * rest sit behind a "+N" trigger that opens a popover listing them. The
 * strip measures itself (hidden zero-height copy + ResizeObserver, the
 * {@link SegmentedTabs} pattern), so when the row runs out of room it drops
 * to fewer chips — never below one — instead of crowding the title. When even
 * that single chip can't fit with its `#number`/id label, the visible chips
 * collapse to just their icon and border; the label stays reachable in each
 * chip's aria label and hover card.
 */
export function AgentAssociationChips({
  associations,
  max = 3,
  agentId,
  eagerVerifyOpenPrs,
  className,
}: AgentAssociationChipsProps) {
  const { associationChips } = usePlugins();
  const rootRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [metrics, setMetrics] = useState<FitMetrics | null>(null);

  const recompute = useCallback(() => {
    const root = rootRef.current;
    const measure = measureRef.current;
    if (!root || !measure) return;
    const chipWidths = Array.from(
      measure.querySelectorAll<HTMLElement>('[data-measure="chip"]'),
      (el) => el.getBoundingClientRect().width,
    );
    const chipIconWidths = Array.from(
      measure.querySelectorAll<HTMLElement>('[data-measure="chip-icon"]'),
      (el) => el.getBoundingClientRect().width,
    );
    const moreWidth =
      measure.querySelector<HTMLElement>('[data-measure="more"]')?.getBoundingClientRect().width ??
      0;
    const next = {
      available: root.getBoundingClientRect().width,
      chipWidths,
      chipIconWidths,
      moreWidth,
    };
    setMetrics((prev) => (prev && sameMetrics(prev, next) ? prev : next));
  }, []);

  // Measure before paint so a constrained strip never flashes its full
  // width, and re-measure whenever the association set changes.
  useLayoutEffect(() => {
    recompute();
  }, [recompute, associations]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(recompute);
    observer.observe(root);
    return () => observer.disconnect();
  }, [recompute]);

  // Fit labelled chips first; if even the single highest-priority chip can't
  // fit the row with its `#number`/id label, collapse every visible chip to
  // its icon and border (the label survives in the aria label and hover card).
  // Icon-only chips are narrower and uniform, so re-fit against those widths.
  const labelledCount = fitCount(associations.length, max, metrics, metrics?.chipWidths);
  const iconOnly =
    !!metrics &&
    metrics.chipWidths.length > 0 &&
    metrics.available > 0 &&
    labelledCount <= 1 &&
    metrics.chipWidths[0] > metrics.available + 1;
  const visibleCount = iconOnly
    ? fitCount(associations.length, max, metrics, metrics.chipIconWidths)
    : labelledCount;
  const visible = associations.slice(0, visibleCount);
  const overflow = associations.slice(visibleCount);
  const candidates = associations.slice(0, Math.min(associations.length, max));

  // On the eager surfaces (the focused stream header + detail identity), batch
  // the live-state fetch for the `open` PR chips into a single `pulls/batch`
  // request via `PullRequestBatchProvider` instead of letting each chip fan out
  // its own `pulls/:number` call — the Sentry AGENT-FLOW-WEB-4F N+1. Scoped to
  // `visible` (the chips that actually render an AgentPRChip); the overflow
  // rows render plain titles with no fetch, so batching them would fetch live
  // state nothing reads. List rows pass `eagerVerifyOpenPrs={false}` and skip
  // the batch so a page of N agents doesn't fire N background fetches; their
  // chips stay lazy (hover-triggered) as before.
  const openPrRefs = openPrRefsForChips(visible, eagerVerifyOpenPrs);

  if (associations.length === 0) return null;

  const renderChip = (ref: AgentAssociationRef, iconOnly: boolean) => {
    if (ref.kind === ASSOCIATION_KIND_PULL_REQUEST) {
      const url = associationUrl(ref);
      if (url) {
        const stored = prAssociationSnapshot(ref);
        return (
          <AgentPRChip
            key={associationKey(ref)}
            prUrl={url}
            role={narrowPrAssociationRole(ref.role)}
            state={stored.state}
            stateUpdatedAt={stored.updatedAt}
            iconOnly={iconOnly}
            eagerVerifyOpenPrs={eagerVerifyOpenPrs}
          />
        );
      }
      // A PR ref with no resolvable URL (unparsable legacy id): still show it.
      return (
        <GenericAssociationChip key={associationKey(ref)} association={ref} iconOnly={iconOnly} />
      );
    }
    if (ref.kind === ASSOCIATION_KIND_AGENT_GROUP) {
      return (
        <AgentGroupChip
          key={associationKey(ref)}
          association={ref}
          agentId={agentId}
          iconOnly={iconOnly}
        />
      );
    }
    if (ref.kind === ASSOCIATION_KIND_AGENT_LABEL) {
      return (
        <AgentLabelChip
          key={associationKey(ref)}
          association={ref}
          agentId={agentId}
          iconOnly={iconOnly}
        />
      );
    }
    const registered = associationChips.get(ref.kind);
    if (registered) {
      return (
        <registered.component key={associationKey(ref)} association={ref} iconOnly={iconOnly} />
      );
    }
    return (
      <GenericAssociationChip key={associationKey(ref)} association={ref} iconOnly={iconOnly} />
    );
  };

  // Whether a "+N" trigger can ever appear at full width: only when there are
  // more associations than the cap. When it can't, the trigger must still be
  // measured (a narrow row can force overflow even under the cap), but it must
  // not pin the root's intrinsic width — otherwise `w-fit` reserves the
  // trigger's width as dead trailing space beside a strip that never overflows.
  const pinsOverflowWidth = associations.length > max;

  // The highest-priority chip is never hidden (fitCount floors at one). `w-fit`
  // + `min-w-0` lets a flex parent squeeze the strip to zero, at which point
  // that single chip — a badge that cannot itself shrink — paints past the
  // strip's box and over whatever sits beside it in the header (the sandbox
  // badge overlap regression). Floor the strip at the measured width of that
  // first chip so it can still collapse the *count* under pressure (the
  // documented behaviour) but never below one visible chip, forcing the
  // adjacent title (which truncates) to give way first instead. The floor is
  // exactly one chip, so a lone chip still hugs the strip's right edge with no
  // trailing dead space. Floor at the *icon-only* width of that chip: it's the
  // narrowest the chip can ever render (labels drop under pressure), so the
  // strip can keep shrinking — and the title keep giving way — right down to a
  // lone icon before anything is forced to overlap.
  const oneChipFloor =
    metrics && metrics.chipIconWidths.length > 0
      ? metrics.chipIconWidths[0]
      : metrics && metrics.chipWidths.length > 0
        ? metrics.chipWidths[0]
        : undefined;

  return (
    <span
      ref={rootRef}
      data-testid="agent-association-chips"
      className={cn('flex w-fit min-w-0 max-w-full flex-col', className)}
      style={oneChipFloor ? { minWidth: oneChipFloor } : undefined}
    >
      {/* Hidden zero-height copy of the widest allowed strip. In-flow, so it
          (a) provides per-chip widths for the fit computation and (b) pins
          the root's intrinsic width to the capped chip set, letting flex
          clamp the root to exactly the available space. */}
      <span ref={measureRef} aria-hidden className="pointer-events-none invisible h-0">
        <span className="flex w-max items-center gap-1">
          {candidates.map((ref) => (
            <MeasureChip key={associationKey(ref)} refItem={ref} />
          ))}
          {/* Measured at the widest count it could show ("+N" for all-but-one
              hidden) so a shrinking strip never underestimates the trigger.
              Taken out of flow unless the strip can actually overflow at full
              width, so it stays measurable without reserving trailing width. */}
          <span
            data-measure="more"
            data-label={`+${Math.max(1, associations.length - 1)}`}
            className={cn(
              badgeVariants({ variant: 'outline' }),
              OVERFLOW_BADGE_CLASSNAME,
              'before:content-[attr(data-label)]',
              pinsOverflowWidth ? undefined : 'absolute',
            )}
          />
        </span>
      </span>
      <span className="flex items-center gap-1">
        {openPrRefs.length > 0 ? (
          <PullRequestBatchProvider prs={openPrRefs}>
            {visible.map((ref) => renderChip(ref, iconOnly))}
            {overflow.length > 0 ? <AssociationOverflow overflow={overflow} /> : null}
          </PullRequestBatchProvider>
        ) : (
          <>
            {visible.map((ref) => renderChip(ref, iconOnly))}
            {overflow.length > 0 ? <AssociationOverflow overflow={overflow} /> : null}
          </>
        )}
      </span>
    </span>
  );
}

const OVERFLOW_BADGE_CLASSNAME =
  'cursor-pointer gap-0.5 border-border bg-muted/50 px-1.5 py-0 text-[10px] font-medium text-muted-foreground hover:bg-muted';

function OverflowTriggerBadge({ count }: { count: number }) {
  return (
    <Badge variant="outline" className={OVERFLOW_BADGE_CLASSNAME}>
      +{count}
    </Badge>
  );
}

/** "+N" chip opening the full list of hidden associations. */
function AssociationOverflow({ overflow }: { overflow: AgentAssociationRef[] }) {
  return (
    <Popover>
      <PopoverTrigger
        aria-label={`Show ${overflow.length} more linked ${overflow.length === 1 ? 'item' : 'items'}`}
        data-testid="agent-association-overflow-trigger"
        className="inline-flex items-center rounded-md align-middle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={(e) => e.stopPropagation()}
      >
        <OverflowTriggerBadge count={overflow.length} />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-1.5"
        data-testid="agent-association-overflow-popover"
      >
        <ul className="flex flex-col">
          {overflow.map((ref) => {
            const url = associationUrl(ref);
            const title = associationDataString(ref, 'title');
            const isPr = ref.kind === ASSOCIATION_KIND_PULL_REQUEST;
            const prMeta = isPr ? PR_STATE_META[prAssociationState(ref)] : null;
            const PrIcon = prMeta?.icon ?? GitPullRequest;
            const FallbackIcon =
              ref.kind === ASSOCIATION_KIND_AGENT_GROUP
                ? Folder
                : ref.kind === ASSOCIATION_KIND_AGENT_LABEL
                  ? Tag
                  : CheckSquare;
            const row = (
              <>
                {isPr && prMeta ? (
                  <PrIcon
                    className={cn('h-3.5 w-3.5 shrink-0', prMeta.iconColor)}
                    aria-hidden="true"
                  />
                ) : (
                  <FallbackIcon
                    className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                )}
                <span className="shrink-0 font-medium text-foreground">
                  {associationLabel(ref)}
                </span>
                {title ? (
                  <span className="min-w-0 truncate text-muted-foreground">{title}</span>
                ) : null}
                {url ? (
                  <ExternalLink
                    className="ml-auto h-3 w-3 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                ) : null}
              </>
            );
            return (
              <li key={associationKey(ref)}>
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="agent-association-overflow-item"
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-accent"
                  >
                    {row}
                  </a>
                ) : (
                  <span
                    data-testid="agent-association-overflow-item"
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs"
                  >
                    {row}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
